import stores from '../stores';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { version } from '../../package.json';
import * as Sentry from '@sentry/react';

function encodeAxiosParam(value) {
    return encodeURIComponent(value)
        .replace(/%3A/gi, ':')
        .replace(/%24/g, '$')
        .replace(/%2C/gi, ',')
        .replace(/%20/g, '+')
        .replace(/%5B/gi, '[')
        .replace(/%5D/gi, ']');
}

// Axios 1 serializes nested query values as filter[status]=open.
// 0.27 JSON-stringified them. Keep that encoding so existing API filters stay valid.
function legacyParamsSerializer(params) {
    const parts = [];
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === null || typeof value === 'undefined') return;
        const values = Array.isArray(value) ? value : [value];
        const renderedKey = Array.isArray(value) ? `${key}[]` : key;
        values.forEach((item) => {
            let rendered = item;
            if (rendered instanceof Date) rendered = rendered.toISOString();
            else if (rendered !== null && typeof rendered === 'object') {
                rendered = JSON.stringify(rendered);
            }
            parts.push(
                `${encodeAxiosParam(renderedKey)}=${encodeAxiosParam(rendered)}`
            );
        });
    });
    return parts.join('&');
}

const API_ROOT = import.meta.env.REACT_APP_API_URL;
const DOMAIN =
    import.meta.env.REACT_APP_DOMAIN ||
    window.localStorage.getItem('custom_domain');
const appId = uuid();
const appVersion = version;

Sentry.setContext('app', {
    requestId: `${appId}`,
    appVersion: `${appVersion}`,
    appAccountDomain: `${DOMAIN || window.location.host}`,
});

class BaseService {
    handleErrors(err) {
        if (err && err.response && err.response.status === 401) {
            stores.authStore.logout();
        }
        return err;
    }

    commonHeaders() {
        let headers = {
            'X-App-Request-Id': `${appId}`,
            'X-App-Version': `${appVersion}`,
            'X-App-Account-Domain': `${DOMAIN || window.location.host}`,
            'X-App-Client-Location': window.location.toString(),
        };

        if (stores.authStore.token)
            headers['x-access-token'] = `${stores.authStore.token}`;
        return { headers };
    }

    async del(url, body) {
        try {
            let response = await axios.delete(url, {
                baseURL: API_ROOT,
                ...this.commonHeaders(),
                data: body,
                paramsSerializer: legacyParamsSerializer,
            });
            return response.data;
        } catch (e) {
            let notHandled = this.handleErrors(e);
            if (notHandled) throw notHandled;
        }
    }

    async get(url, params, options = {}) {
        try {
            let response = await axios.get(url, {
                params,
                ...options,
                baseURL: API_ROOT,
                ...this.commonHeaders(),
                paramsSerializer: legacyParamsSerializer,
            });
            if (options && options.responseType === 'blob') return response;
            return response.data;
        } catch (e) {
            let notHandled = this.handleErrors(e);
            if (notHandled) throw notHandled;
        }
    }

    async put(url, body, options) {
        try {
            let response = await axios.put(url, body, {
                baseURL: API_ROOT,
                ...this.commonHeaders(),
                ...options,
                paramsSerializer: legacyParamsSerializer,
            });
            return response.data;
        } catch (e) {
            let notHandled = this.handleErrors(e);
            if (notHandled) throw notHandled;
        }
    }

    async post(url, body, options) {
        try {
            let config = {
                baseURL: API_ROOT,
                ...this.commonHeaders(),
                paramsSerializer: legacyParamsSerializer,
            };
            let response = await axios.post(url, body, config);
            return response.data;
        } catch (e) {
            let notHandled = this.handleErrors(e);
            if (notHandled) throw notHandled;
        }
    }
}

export default BaseService;
